"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import { Card, CardContent } from "@/components/ui/card";
import { Globe, Server } from "lucide-react";

interface Location {
  country: string;
  cities?: string[];
  serverCount?: string;
}

interface ServerLocationsProps {
  title?: string;
  subtitle?: string;
  locations: Location[];
}

const ServerLocations = ({ title, subtitle, locations }: ServerLocationsProps) => {
  if (!locations || locations.length === 0) return null;

  return (
    <section className="py-24 px-6 bg-muted/50">
      <div className="container max-w-5xl mx-auto">
        {/* Header */}
        {(title || subtitle) && (
          <div className="text-center mb-16 space-y-4">
            {title && (
              <BlurFade delay={0.1}>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                  {title}
                </h2>
              </BlurFade>
            )}
            {subtitle && (
              <BlurFade delay={0.15}>
                <p className="text-foreground/60 text-lg max-w-2xl mx-auto">
                  {subtitle}
                </p>
              </BlurFade>
            )}
          </div>
        )}

        {/* Location Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {locations.map((location, i) => (
            <BlurFade key={i} delay={0.15 + i * 0.03}>
              <Card className="border-foreground/5 bg-white hover:shadow-md transition-shadow h-full">
                <CardContent className="p-6 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-secondary flex items-center justify-center">
                        <Globe size={18} className="text-foreground/70" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-base font-bold text-foreground">
                        {location.country}
                      </h3>
                    </div>
                    {location.serverCount && (
                      <div className="flex items-center gap-1.5 text-xs font-medium text-foreground/40">
                        <Server size={12} strokeWidth={2} />
                        <span>{location.serverCount}</span>
                      </div>
                    )}
                  </div>
                  {location.cities && location.cities.length > 0 && (
                    <p className="text-foreground/50 text-sm leading-relaxed pl-12">
                      {location.cities.join(", ")}
                    </p>
                  )}
                </CardContent>
              </Card>
            </BlurFade>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServerLocations;
